import React from 'react';
import { Box, Flex, Text, useDisclosure } from '@chakra-ui/react';
import { createColumnHelper } from '@tanstack/react-table';
import CommonPagination from 'components/commonPagination/CommonPagination';
import CommonTable from 'components/commonTable/CommonTable';
import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import useListState from 'hooks/useListState';
import { useColorModeVariables } from '../../../components/colorModeVarialbels/ColorModeValues';
import DeleteModal from '../../../components/popUpModel/DeleteModal';
import {
  DeleteUserData,
  ListUserData,
} from '../../../reactRedux/thunks/userThunk/userThunk';
import CommonSearch from 'components/commonSearchBar/CommonSearch';

const UserList = () => {
  const { tabletextColor } = useColorModeVariables();
  const { isOpen, onClose } = useDisclosure();
  const userDeleteRef = useRef(null);
  const { isLoading, userListData } = useSelector((state) => state.UserSlice);
  const dispatch = useDispatch();

  const {
    params,
    setParam,
    setParams,
    debouncedParams,
    page,
    setPage,
    currentUrl,
  } = useListState({
    defaults: { search: '', limit: 10 },
    debounceKeys: ['search'],
  });

  const getUserList = useCallback(() => {
    dispatch(
      ListUserData({
        page: page,
        limit: params.limit || 10,
        search: debouncedParams.search,
      }),
    );
  }, [dispatch, page, params.limit, debouncedParams.search]);

  useEffect(() => {
    getUserList();
  }, [getUserList]);

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor((row) => row?.firstName, {
      id: 'firstName',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          First Name
        </Text>
      ),
      cell: (info) => (
        <Link
          to={`/admin/users/details/${info?.row?.original?.userId}`}
          state={{ returnUrl: `/admin/users${currentUrl}` }}
          className="btn btn-primary rounded-2 p-1"
        >
          <Text
            color={tabletextColor}
            fontSize="sm"
            fontWeight="600"
            align="left"
            whiteSpace="nowrap"
            overflow={'hidden'}
            textOverflow={'ellipsis'}
          >
            {info.getValue()}
          </Text>
        </Link>
      ),
    }),
    columnHelper.accessor((row) => row?.lastName, {
      id: 'lastName',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Last Name
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue()}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.email, {
      id: 'email',
      header: () => (
        <Text
          whiteSpace="nowrap"
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
        >
          Email
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() ? info.getValue() : 'N/A'}
        </Text>
      ),
    }),
    columnHelper.accessor((row) => row?.phone, {
      id: 'phone',
      header: () => (
        <Text
          align="left"
          fontSize={{ sm: '10px', lg: '12px' }}
          color="gray.400"
          fontFamily="Montserrat"
          whiteSpace="nowrap"
        >
          Contact Number
        </Text>
      ),
      cell: (info) => (
        <Text
          color={tabletextColor}
          fontSize="sm"
          fontWeight="600"
          align="left"
          whiteSpace="nowrap"
          overflow={'hidden'}
          textOverflow={'ellipsis'}
        >
          {info.getValue() ? `+${info.getValue()}` : ''}
        </Text>
      ),
    }),
  ];

  return (
    <>
      <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
        <Flex justify="flex-end" mb={4} gap="20px">
          <CommonSearch
            search={params.search}
            setSearch={(val) => setParam('search', val)}
            setPage={setPage}
          />
          {/* <Button
            bg="reviveOrange"
            color="primary"
            borderRadius="12px"
            _hover={{ bg: 'reviveOrange', opacity: 0.8 }}
            onClick={() => navigate('/admin/users/add')}
          >
            <Icon
              as={FaPlus}
              width="15px"
              height="15px"
              marginRight="5px"
              color="inherit"
            />
            Add User
          </Button> */}
        </Flex>
        <CommonTable
          columns={columns}
          data={userListData?.list}
          isLoading={isLoading}
          keyValue="userId"
          isLink={true}
          parentLink="/admin/users/details"
          rowclick={['actions']}
          dynamicWidth={{
            firstName: '200px',
            lastName: '200px',
            email: '300px',
            phone: '200px',
          }}
        />

        {/* {userListData?.total > 10 && ( */}
        <CommonPagination
          totalCount={userListData?.total}
          activePage={page}
          pageCount={Math.ceil(userListData?.total / userListData?.limit)}
          onPageChange={setPage}
          itemsPerPage={params.limit || 10}
          onItemsPerPageChange={(newLimit) => {
            setParams({ limit: newLimit, page: 1 });
          }}
        />
        {/* )} */}

        <DeleteModal
          isOpen={isOpen}
          onClose={onClose}
          title="Are you sure you want to delete this user?"
          SolidBTNText="Delete"
          onDelete={() => {
            dispatch(
              DeleteUserData(userDeleteRef.current, {}, () => {
                if (page > 1 && userListData?.list?.length === 1) {
                  setPage(page - 1);
                } else {
                  getUserList();
                }
                onClose();
              }),
            );
          }}
          loader={isLoading}
        />
      </Box>
    </>
  );
};

export default UserList;
